import { Component, Inject, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { formatCarWashDisplayName, initialsFromCarWashName } from './feedback-branding.util';
import { FEEDBACK_LOCATION_ASSIGNER, FeedbackLocationAssigner } from './feedback-location.token';
import { FeedbackPublicService } from './feedback-public.service';

/** Steps match the business feedback SMS prototype flow. */
export type FeedbackStep = 'rate' | 'thanks' | 'invalid' | 'already_submitted';

const STAR_VALUES = [1, 2, 3, 4, 5] as const;

/** Ratings below this threshold require a comment and hide the Google review prompt. */
const HIGH_RATING_MIN = 4;

const PREVIEW_STATES = new Set<FeedbackStep>(['thanks', 'invalid', 'already_submitted']);

/** g.page, goo.gl, google.com, and country Maps hosts (e.g. maps.google.co.uk). */
const GOOGLE_REVIEW_HOST = /^(?:(?:[\w-]+\.)*(?:g\.page|goo\.gl|google(?:\.[a-z]{2,}){1,2}))$/i;

/** Allow only https Google review URLs from the SMS `googleUrl` query. */
export function sanitizeGoogleReviewUrl(raw: string): string {
  try {
    const url = new URL(raw.trim());
    if (url.protocol !== 'https:' || !GOOGLE_REVIEW_HOST.test(url.hostname)) {
      return '';
    }
    url.username = '';
    url.password = '';
    return url.href;
  } catch {
    return '';
  }
}

/**
 * Public mobile feedback form opened from post-wash SMS links.
 *
 * SMS URL shape:
 *   /{slug}/feedback?feedbackId={uuid}&token={feedbackToken}
 * Optional until branding API exists:
 *   &name=…&googleUrl=…   — explicit branding overrides
 *   &state=thanks|…       — design previews
 */
@Component({
  selector: 'app-feedback-form-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './feedback-form-page.component.html',
  styleUrl: './feedback-form-page.component.scss',
})
export class FeedbackFormPageComponent implements OnInit, OnDestroy {
  readonly stars = STAR_VALUES;

  /** Resolved from SMS path/query and public feedback context API. */
  readonly carWashName = signal('');
  readonly logoUrl = signal('');
  readonly logoInitials = signal('');
  readonly googleUrl = signal('');
  /** Location feedback_slug from the SMS link. */
  readonly locationSlug = signal('');
  /** Feedbacks row UUID from the SMS link. */
  readonly feedbackId = signal('');
  /** Opaque feedback token from the SMS link. */
  readonly feedbackToken = signal('');

  readonly rating = signal(0);
  readonly comment = signal('');
  readonly step = signal<FeedbackStep>('rate');
  readonly submitting = signal(false);
  readonly submitError = signal('');
  readonly loadingContext = signal(false);
  /** True when the browser blocks window.close() (typical for SMS deep links). */
  readonly closeBlocked = signal(false);

  readonly isHighRating = computed(() => this.rating() >= HIGH_RATING_MIN);

  readonly showCommentBox = computed(() => {
    const rating = this.rating();
    return rating >= 1 && rating < HIGH_RATING_MIN;
  });

  readonly showContinue = computed(() => this.rating() >= 1 && this.step() === 'rate');

  readonly showGoogleReviewLink = computed(
    () => this.step() === 'thanks' && this.isHighRating() && Boolean(this.googleUrl())
  );

  private routeSub?: Subscription;
  private submitSub?: Subscription;
  private contextSub?: Subscription;
  private lastFeedbackKey = '';

  constructor(
    private route: ActivatedRoute,
    private feedbackPublic: FeedbackPublicService,
    @Inject(FEEDBACK_LOCATION_ASSIGNER) private readonly assignLocation: FeedbackLocationAssigner
  ) {}

  ngOnInit(): void {
    this.routeSub = combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
      ([params, query]) => {
        this.applyRouteContext(params, query);
      }
    );
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.submitSub?.unsubscribe();
    this.contextSub?.unsubscribe();
  }

  pickRating(value: number): void {
    if (this.step() !== 'rate' || this.submitting()) {
      return;
    }
    this.submitError.set('');
    if (value === this.rating()) {
      this.rating.set(0);
      this.comment.set('');
      return;
    }
    this.rating.set(value);
    if (value >= HIGH_RATING_MIN) {
      this.comment.set('');
    }
  }

  onCommentInput(value: string): void {
    this.comment.set(value);
    if (this.submitError()) {
      this.submitError.set('');
    }
  }

  onSubmit(): void {
    if (this.step() !== 'rate' || this.rating() < 1 || this.submitting()) {
      return;
    }

    if (this.rating() < HIGH_RATING_MIN && !this.comment().trim()) {
      this.submitError.set('Please tell us what we could do better');
      return;
    }

    const feedbackId = this.feedbackId();
    const token = this.feedbackToken();
    if (!feedbackId || !token) {
      this.step.set('invalid');
      return;
    }

    this.submitting.set(true);
    this.submitError.set('');
    this.submitSub?.unsubscribe();
    this.submitSub = this.feedbackPublic
      .submit({
        feedbackId,
        token,
        rating: this.rating(),
        comment: this.showCommentBox() ? this.comment() : '',
      })
      .subscribe({
        next: response => {
          this.submitting.set(false);
          this.googleUrl.set(sanitizeGoogleReviewUrl(response?.data?.googleReviewUrl || ''));
          this.step.set('thanks');
        },
        error: (error: unknown) => {
          this.submitting.set(false);
          this.handleSubmitError(error);
        },
      });
  }

  openGoogleReview(event?: Event): void {
    event?.preventDefault();
    const url = sanitizeGoogleReviewUrl(this.googleUrl());
    if (!url) {
      return;
    }
    this.navigateToGoogleReview(url);
  }

  /** Same-tab navigation so mobile browsers do not treat it as a popup. */
  navigateToGoogleReview(url: string): void {
    this.assignLocation(url);
  }

  closeFeedback(): void {
    window.close();
    if (window.closed) {
      return;
    }
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    this.closeBlocked.set(true);
  }

  onLogoError(): void {
    this.logoUrl.set('');
  }

  /**
   * Apply car-wash branding + tokens from the SMS URL.
   * Prefer explicit `name` / `googleUrl` query overrides; otherwise derive from `/:carwashSlug`.
   */
  private applyRouteContext(params: ParamMap, query: ParamMap): void {
    const pathSlug = params.get('carwashSlug')?.trim() || '';
    const locationSlug = query.get('slug')?.trim() || '';
    const feedbackId = query.get('feedbackId')?.trim() || '';
    const token = query.get('token')?.trim() || '';
    const nameFromQuery = query.get('name')?.trim() || '';
    const displayName = formatCarWashDisplayName(nameFromQuery || pathSlug);
    const feedbackKey = `${pathSlug}|${locationSlug}|${feedbackId}|${token}`;

    if (feedbackKey !== this.lastFeedbackKey) {
      this.lastFeedbackKey = feedbackKey;
      this.resetInteractiveState();
    }

    this.carWashName.set(displayName);
    this.logoInitials.set(displayName ? initialsFromCarWashName(displayName) : '');
    this.googleUrl.set(sanitizeGoogleReviewUrl(query.get('googleUrl') || ''));
    this.locationSlug.set(locationSlug);
    this.feedbackId.set(feedbackId);
    this.feedbackToken.set(token);

    const previewState = query.get('state')?.trim() as FeedbackStep | undefined;
    if (previewState && PREVIEW_STATES.has(previewState)) {
      this.step.set(previewState);
      return;
    }

    if (feedbackId && token) {
      this.loadPublicContext(feedbackId, token);
    }
  }

  private loadPublicContext(feedbackId: string, token: string): void {
    this.contextSub?.unsubscribe();
    this.loadingContext.set(true);
    this.contextSub = this.feedbackPublic.loadContext({ feedbackId, token }).subscribe({
      next: response => {
        this.loadingContext.set(false);
        const data = response?.data;
        if (data?.companyName) {
          this.carWashName.set(data.companyName);
          this.logoInitials.set(initialsFromCarWashName(data.companyName));
        }
        if (data?.companyLogo) {
          this.logoUrl.set(data.companyLogo);
        }
        const reviewUrl = sanitizeGoogleReviewUrl(data?.googleReviewUrl || '');
        if (reviewUrl) {
          this.googleUrl.set(reviewUrl);
        }
      },
      error: (error: unknown) => {
        this.loadingContext.set(false);
        this.handleContextError(error);
      },
    });
  }

  private handleContextError(error: unknown): void {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 409) {
      this.step.set('already_submitted');
      return;
    }
    if (status === 404 || status === 410) {
      this.step.set('invalid');
    }
  }

  private handleSubmitError(error: unknown): void {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 409) {
      this.step.set('already_submitted');
      return;
    }
    if (status === 404 || status === 410) {
      this.step.set('invalid');
      return;
    }
    const message =
      error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message
        : 'Unable to submit feedback. Please try again.';
    this.submitError.set(message);
  }

  private resetInteractiveState(): void {
    this.submitSub?.unsubscribe();
    this.contextSub?.unsubscribe();
    this.submitting.set(false);
    this.loadingContext.set(false);
    this.submitError.set('');
    this.rating.set(0);
    this.comment.set('');
    this.logoUrl.set('');
    this.step.set('rate');
    this.closeBlocked.set(false);
  }
}
